/**
 * Places a person can say they are, as codes rather than names.
 *
 * **Codes, because a name is a rendering.** `Intl.DisplayNames` turns `IL` into
 * whatever the reader's own locale calls it, so storing the code means the list
 * is never a translation to maintain and never goes stale in one language while
 * being right in another. It also means the stored value survives a country
 * being renamed, which names do not.
 *
 * The list is ISO 3166-1 alpha-2. What it is FOR is narrow — improving an
 * address lookup, and answering whether somebody keeps one day of Yom Tov or
 * two — so completeness matters more than curation: a list that omitted
 * somewhere would simply have no answer for whoever lives there.
 */
export const COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const

export type CountryCode = (typeof COUNTRY_CODES)[number]

/** Israel, which decides one day of Yom Tov rather than two. */
export const ISRAEL: CountryCode = 'IL'

/**
 * The US's states, DC and territories, by their postal codes.
 *
 * A US or Canadian address is not usable without its state or province, the
 * set is closed and short, and everybody writing one expects to pick from it —
 * so it is picked, and stored as its code. An address anywhere else names no
 * state (`regionFor`).
 */
export const US_SUBDIVISIONS: readonly { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
  { code: 'GU', name: 'Guam' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'MP', name: 'Northern Mariana Islands' },
]

/** Canada's provinces and territories, by their postal codes. */
export const CA_SUBDIVISIONS: readonly { code: string; name: string }[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
]

/**
 * Every region an address picks from: the US's states, DC and territories and
 * Canada's provinces and territories. The one list — an address input offers
 * it, and two addresses compare a region by it. No code appears in both
 * countries, so a code alone names its region.
 */
export const REGIONS: readonly {
  code: string
  name: string
  country: 'US' | 'CA'
}[] = [
  ...US_SUBDIVISIONS.map((r) => ({ ...r, country: 'US' as const })),
  ...CA_SUBDIVISIONS.map((r) => ({ ...r, country: 'CA' as const })),
]

const REGION_CODE = new Map(
  REGIONS.flatMap((r) => [
    [r.code.toLowerCase(), r.code] as const,
    [r.name.toLowerCase(), r.code] as const,
  ]),
)

/** A region's code from its code or its full name, in any case and with
 *  surrounding spaces ignored: "nj" and " New Jersey " are both `NJ`. Null
 *  for anything not on the list. */
export const regionCode = (raw: string | null | undefined): string | null =>
  REGION_CODE.get((raw ?? '').trim().toLowerCase()) ?? null

/** The countries whose addresses name a region from the list. */
export type RegionCountry = (typeof REGIONS)[number]['country']

/** The country a region on the list belongs to, from its code or its name;
 *  null for anything not on the list. */
export const countryOfRegion = (
  raw: string | null | undefined,
): RegionCountry | null => {
  const code = regionCode(raw)
  return REGIONS.find((r) => r.code === code)?.country ?? null
}

/** An address's region as stored, or why it cannot be. */
export type RegionRead = { region: string | null } | { refused: string }

const NOT_ON_LIST: Record<RegionCountry, string> = {
  US: 'That is not a US state.',
  CA: 'That is not a Canadian province or territory.',
}

/**
 * The one rule for an address's region, given its country.
 *
 * A US or Canadian address names its state or province from that country's
 * list, and it is stored as its code — "New Jersey" and "nj" are both `NJ`.
 * Anywhere else an address has no state, so a region given there is refused
 * rather than kept as text nobody can compare. A blank region is no region,
 * in every country: it is optional, and an address may not have one yet.
 */
export function regionFor(
  country: string,
  raw: string | null | undefined,
): RegionRead {
  if (!(raw ?? '').trim()) return { region: null }
  if (country !== 'US' && country !== 'CA') {
    return { refused: 'Only a US or Canadian address names a state.' }
  }
  const code = regionCode(raw)
  return code && countryOfRegion(code) === country
    ? { region: code }
    : { refused: NOT_ON_LIST[country] }
}

/** Whether this country keeps one day of Yom Tov. */
export const isIsrael = (country: string | null | undefined): boolean =>
  country === ISRAEL
